/**
 * Sibling-reference helpers shared by the Firestore and Algolia fetch paths.
 *
 * Lives in its own module (rather than in firebase.ts) so the pure list-building
 * logic is unit-testable without pulling in the Firebase SDK, and so algolia.ts
 * can reuse it without importing firebase.ts.
 *
 * The selection rules themselves are in `notableVariantCandidates.ts`; this
 * module only adapts Algolia hits to them.
 */
import {
  MAX_SIBLING_REFERENCES,
  MIN_SIBLING_REFERENCES,
  siblingReferencesFor,
  type SiblingReference,
  type SiblingSubject,
} from './notableVariantCandidates';

export { MAX_SIBLING_REFERENCES, MIN_SIBLING_REFERENCES };
export type { SiblingReference, SiblingSubject };

/**
 * The subset of an Algolia `watch_references` hit needed to build the sibling
 * list. `referenceId` is the Firestore doc id (what the subject's `id` is
 * compared against); `objectID` is the index's own key and is checked too, so a
 * caller holding either id form still excludes the ref being enriched.
 */
export interface SiblingReferenceHit {
  objectID: string;
  referenceId?: string;
  reference?: string;
  model?: string;
  nickname?: string;
  collection?: string;
  /** Flattened `production.releaseYear` / `startYear` on the refs index record. */
  releaseYear?: number;
  startYear?: number;
}

/**
 * Build the sibling list from Algolia hits, by the same rules the Firestore
 * path uses: same collection as the subject, not the subject, one entry per
 * reference, at least {@link MIN_SIBLING_REFERENCES} or nothing.
 *
 * The collection is re-checked here even though the search filtered on it: the
 * search has to ask for several casings of a free-text value, and this is the
 * comparison that actually decides.
 *
 * No variant filtering is needed for hits — the index carries PARENTS AND
 * STANDALONES ONLY (`syncReferenceToAlgolia` evicts any ref that gains a
 * `parentReferenceId`) — but the selector applies it anyway and it is a no-op.
 */
export function siblingReferencesFromHits(
  hits: readonly SiblingReferenceHit[],
  subject: SiblingSubject,
): SiblingReference[] {
  return siblingReferencesFor(
    subject,
    hits
      // A hit is the subject if EITHER of its ids is the subject's.
      .filter((hit) => !subject.id || hit.objectID !== subject.id)
      .map((hit) => ({
        id: hit.referenceId,
        reference: hit.reference,
        model: hit.model,
        nickname: hit.nickname,
        collection: hit.collection,
        releaseYear: hit.releaseYear,
        startYear: hit.startYear,
      })),
  );
}

/** What an index lookup found — see `getBrandSiblingReferences`. */
export interface IndexedSiblingReferences {
  siblings: SiblingReference[];
  /** Records the index returned for the brand + collection, subject included. */
  indexedInCollection: number;
}

/**
 * Decide where a sibling list comes from: the index, or the database behind it.
 *
 * - No collection on the subject → no siblings, and NEITHER source is asked.
 * - The index answers, and holds at least one record for the collection → that
 *   answer is final, even when it is "too few siblings". A collection of one or
 *   two is common, and re-reading the whole brand to confirm it every time
 *   would undo what the index path is for.
 * - The index holds NOTHING for the collection — not even the subject — or is
 *   unavailable, or throws → ask the database. Nothing-at-all means "not
 *   indexed" (an unsaved ref, or a disabled brand whose refs are all evicted),
 *   not "no siblings".
 *
 * The two sources are injected so this can be tested on its own.
 */
export async function resolveSiblingReferences(params: {
  subject: SiblingSubject;
  /** Omit when the index is not configured. */
  fromIndex?: () => Promise<IndexedSiblingReferences>;
  fromDatabase: () => Promise<SiblingReference[]>;
  warn?: (message: string, error?: unknown) => void;
}): Promise<SiblingReference[]> {
  const { subject, fromIndex, fromDatabase, warn = () => {} } = params;
  if (typeof subject.collection !== 'string' || !subject.collection.trim()) return [];
  if (fromIndex) {
    try {
      const { siblings, indexedInCollection } = await fromIndex();
      if (indexedInCollection > 0) return siblings;
      warn(
        `[siblings] the index holds nothing for collection "${subject.collection}" ` +
          '(brand or ref may not be indexed) — falling back to Firestore',
      );
    } catch (error) {
      warn('[siblings] index lookup failed, falling back to Firestore', error);
    }
  }
  return fromDatabase();
}
